/* eslint-disable @typescript-eslint/ban-types */
import React, { useEffect, useRef, useState } from 'react';
import { Sprite } from '@inlet/react-pixi';
import { InteractionEvent, Point, Texture } from 'pixi.js';
import { useNonDragClick } from '../../hooks/useNonDragClick';

import rewardActionsSvg from '../../assets/draggable-rewards/reward-actions.svg';
import moveSvg from '../../assets/draggable-rewards/move.svg';
import movingSvg from '../../assets/draggable-rewards/move-pressed.svg';
import scaleSvg from '../../assets/draggable-rewards/scale.svg';
import scalingSvg from '../../assets/draggable-rewards/scale-pressed.svg';
import binSvg from '../../assets/draggable-rewards/bin.svg';
import flipSvg from '../../assets/draggable-rewards/flip.svg';
import { Scale } from '../island/utils';

const REWARD_ACTIONS_WIDTH = 200;
const REWARD_ACTIONS_HEIGHT = 64;

const ACTION_BUTTON_SIDE_LENGTH = REWARD_ACTIONS_WIDTH / 4 - 5;

export interface IDraggableReward <Meta> {
  id: string;
  name: string;
  sideLength: number;
  flipped: boolean;
  scalePercentage: number;
  selected?: boolean;
  canMove?: boolean;
  canResize?: boolean;
  scale: React.MutableRefObject<Scale>;
  url: string;
  position: Point;
  meta: Meta;
}

interface Props <Meta> {
  onRewardClicked: () => void;
  item: IDraggableReward<Meta>;
  onFinishingMoving: (position: Point) => void;
  onMove: () => void;
  onDelete: () => void;
  onResize: () => void;
  onFlip: () => void;
  worldCentre: Point;
  worldHeight: number;
}

export const DraggableReward = <Meta extends object> ({
  item: {
    url, position: initialPosition, scale,
    selected = false, canMove = false, canResize = false, flipped = false,
    scalePercentage = 50, sideLength,
  },
  onFinishingMoving,
  onRewardClicked,
  onMove, onResize, onDelete, onFlip, worldCentre, worldHeight,
}: Props<Meta>): React.ReactElement => {
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState(initialPosition);
  const [onPointerDown, onPointerUp] = useNonDragClick();
  const lastMousePosition = useRef(new Point());

  useEffect(() => {
    setPosition(initialPosition);
  }, [initialPosition]);

  /**
   * We adjust the side of the reward to the scale the user has selected.
   */
  const scaledRewardSideLength = sideLength * (scalePercentage / 100);

  const rewardTexture = Texture.from(url);
  const flipTexture = Texture.from(flipSvg);

  const onStartMoving = (event: InteractionEvent) => {
    lastMousePosition.current.set(event.data.global.x, event.data.global.y);
    setDragging(true);
  };

  const onStopMoving = () => {
    setDragging(false);
    onFinishingMoving(position);
  };

  const stopPropagation = (callback: (event: InteractionEvent) => void) => (event: InteractionEvent) => {
    event?.stopPropagation();
    callback(event);
  };

  const onDrag = (event: InteractionEvent) => {
    if (!dragging) return;

    const { data: { global: { x, y } } } = event;

    event.stopPropagation();

    const delta = new Point(
      (x - lastMousePosition.current.x),
      (y - lastMousePosition.current.y),
    );

    const maxY = worldCentre.y + (worldHeight / 3);
    const minY = worldCentre.y - (worldHeight / 5);
    const newYPosition = position.y + (delta.y / scale.current.y);
    const newXPosition = position.x + (delta.x / scale.current.x);

    /**
     * @note We set borders/limit to canvasHeight/5 when moving up and canvasHeight/3 when moving
     *       down - effectively limiting the Y axis to about 50% - in order to prevent items being
     *       dragged to areas where the user can no longer access them due to overlaid content
     */
    if (newYPosition > minY && newYPosition < maxY) {
      setPosition(new Point(
        newXPosition,
        newYPosition,
      ));
      lastMousePosition.current.set(x, y);
    }
  };

  /**
   * As we zoom in, the scale increases and so we need to scale the controls to be rendered inversley
   * proportional to that scale.
   */
  const scaledActionButtonSize = ACTION_BUTTON_SIDE_LENGTH / scale.current.y;
  /**
   * The buttons must be offset on the X axis so that they appear over the white actions background.
   * (the background SVG has transparent bits)
   */
  const BUTTONS_OFFSET_X = (REWARD_ACTIONS_WIDTH + 2 - (ACTION_BUTTON_SIDE_LENGTH * 4)) / scale.current.x;

  /**
   * Buttons must be offset on the Y axis by half of their size (as they are anchored at 0.5 which
   * means their centre is in the middle of the button)
   */
  const BUTTONS_OFFSET_Y = REWARD_ACTIONS_HEIGHT / 2 / scale.current.y;
  /**
   * We need to slightly move the background so that the buttons are centered on top of it.
   */
  const REWARD_ACTIONS_BACKGROUND_OFFSET_Y = 2 / scale.current.y;

  const BUTTON_TOP_OFFSET = scaledRewardSideLength / 2 + BUTTONS_OFFSET_Y;

  return (
    <>
      {selected && [
        (<Sprite
          key={0}
          image={rewardActionsSvg}
          anchor={0.5}
          width={REWARD_ACTIONS_WIDTH / scale.current.x}
          height={REWARD_ACTIONS_HEIGHT / scale.current.y}
          x={position.x}
          y={position.y - BUTTON_TOP_OFFSET + REWARD_ACTIONS_BACKGROUND_OFFSET_Y}
        />),
        (<Sprite
          key={1}
          image={canMove ? movingSvg : moveSvg}
          anchor={0.5}
          width={scaledActionButtonSize}
          height={scaledActionButtonSize}
          pointerup={stopPropagation(onMove)}
          interactive
          x={position.x - (scaledActionButtonSize * 2) + BUTTONS_OFFSET_X}
          y={position.y - BUTTON_TOP_OFFSET}
          cursor="pointer"
        />),
        (<Sprite
          key={2}
          image={canResize ? scalingSvg : scaleSvg}
          anchor={0.5}
          width={scaledActionButtonSize}
          height={scaledActionButtonSize}
          pointerup={stopPropagation(onResize)}
          interactive
          x={position.x - scaledActionButtonSize + BUTTONS_OFFSET_X}
          y={position.y - BUTTON_TOP_OFFSET}
          cursor="pointer"
        />),
        (<Sprite
          key={4}
          texture={flipTexture}
          anchor={0.5}
          width={ACTION_BUTTON_SIDE_LENGTH}
          height={ACTION_BUTTON_SIDE_LENGTH}
          pointerdown={stopPropagation(onFlip)}
          interactive
          scale={flipped
            ? { x: (-1 / scale.current.x), y: (1 / scale.current.y) }
            : { x: (1 / scale.current.x), y: (1 / scale.current.y) }}
          x={position.x + BUTTONS_OFFSET_X}
          y={position.y - BUTTON_TOP_OFFSET}
          cursor="pointer"
        />),
        (<Sprite
          key={5}
          image={binSvg}
          anchor={0.5}
          pointerdown={stopPropagation(onDelete)}
          width={scaledActionButtonSize}
          height={scaledActionButtonSize}
          interactive
          x={position.x + scaledActionButtonSize + BUTTONS_OFFSET_X}
          y={position.y - BUTTON_TOP_OFFSET}
          cursor="pointer"
        />),
      ].map((element) => (element))}

      <Sprite
        scale={{ x: flipped ? -1 : 1, y: 1 }}
        texture={rewardTexture}
        width={scaledRewardSideLength}
        height={scaledRewardSideLength}
        pointerdown={(event) => {
          event.stopPropagation();

          if (selected) {
            if (canMove) {
              onStartMoving(event);

              return;
            }

            onPointerDown(event);
          }
        }}
        pointerup={(event) => {
          event.stopPropagation();

          if (selected) {
            if (canMove) {
              onStopMoving();
              return;
            }

            onPointerUp(event, () => onRewardClicked());

            return;
          }

          onRewardClicked();
        }}
        pointermove={onDrag}
        x={position.x}
        y={position.y}
        anchor={0.5}
        interactive
        cursor="pointer"
      />
    </>
  );
};

export default {};
